(() => {
  if (customElements.get('emz-saved-picks')) return;
  const LIMIT = 20;
  const identity = value => {
    if (!value || !/^\d{1,25}$/.test(value.id) || typeof value.title !== 'string' || !value.title.trim() || value.title.length > 500 || typeof value.handle !== 'string' || !/^[\p{L}\p{N}][\p{L}\p{N}-]{0,254}$/u.test(value.handle)) return null;
    return { id: String(value.id), handle: value.handle, title: value.title };
  };
  const failure = code => Object.assign(new Error(code), { code });
  // Web Locks serialize this feature's localStorage mutations across tabs.
  // Without that API, use a separate, explicitly tab-only session list.
  class SavedStore {
    constructor(themeId, themeRole) {
      this.key = `emz:saved-picks:v2:${themeRole === 'main' ? 'storefront' : themeId}`;
      this.mode = 'unavailable';
      if (!/^\d+$/.test(themeId)) return;
      if (navigator.locks?.request) {
        try { this.storage = window.localStorage; this.storage.getItem(this.key); this.mode = 'local'; return; } catch (_) { /* Try tab-only storage. */ }
      }
      try { this.storage = window.sessionStorage; this.key += ':tab'; this.storage.getItem(this.key); this.mode = 'session'; } catch (_) { this.storage = null; }
    }
    read() {
      if (!this.storage) throw failure('unavailable');
      let raw;
      try { raw = this.storage.getItem(this.key); } catch (_) { throw failure('unavailable'); }
      if (raw === null) return [];
      try {
        if (raw.length > 24000) throw failure('read_error');
        const value = JSON.parse(raw);
        if (value?.version !== 2 || !Array.isArray(value.items) || value.items.length > LIMIT) throw failure('read_error');
        const items = value.items.map(identity);
        if (items.some(item => !item) || new Set(items.map(item => item.id)).size !== items.length) throw failure('read_error');
        return items;
      } catch (_) { throw failure('read_error'); }
    }
    async mutate(action, item, active) {
      const commit = () => {
        if (!active()) throw failure('cancelled');
        const items = action === 'reset' ? [] : this.read();
        let next = items;
        if (action === 'add') {
          if (!identity(item)) throw failure('write_error');
          if (!items.some(existing => existing.id === item.id)) {
            if (items.length >= LIMIT) throw failure('limit');
            next = [...items, item];
          }
        } else if (action === 'remove') next = items.filter(existing => existing.id !== item.id);
        else if (action === 'clear' || action === 'reset') next = [];
        else throw failure('write_error');
        if (!this.storage) throw failure('unavailable');
        try { this.storage.setItem(this.key, JSON.stringify({ version: 2, items: next })); }
        catch (_) { throw failure('write_error'); }
        return next;
      };
      if (this.mode !== 'local') return commit();
      const abort = new AbortController();
      const timeout = setTimeout(() => abort.abort(), 5000);
      try { return await navigator.locks.request(this.key, { mode: 'exclusive', signal: abort.signal }, commit); }
      catch (error) { if (error.name === 'AbortError') throw failure('busy'); throw error; }
      finally { clearTimeout(timeout); }
    }
  }
  class SavedPicks extends HTMLElement {
    connectedCallback() {
      this.ticket = (this.ticket || 0) + 1;
      this.ready = () => this.mount();
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', this.ready, { once: true });
      else this.mount();
    }
    mount() {
      if (!this.isConnected || this.mounted) return;
      this.mounted = true;
      try { this.labels = JSON.parse(this.dataset.labels); } catch (_) { return; }
      this.editor = this.dataset.editor === 'true';
      this.store = new SavedStore(String(window.Shopify?.theme?.id || ''), window.Shopify?.theme?.role);
      const root = new URL(this.dataset.rootUrl || '/', location.origin);
      if (root.origin !== location.origin || root.search || root.hash) return;
      this.root = root.pathname.replace(/\/?$/, '/');
      this.items = new Map(); this.details = new Map(); this.pending = new Map();
      this.copyTicket = 0; this.busy = false;
      this.onClick = event => this.click(event);
      this.onReload = () => { this.bindPanel(); this.render(); this.hydrate(); };
      this.onControlsReady = () => this.syncControls();
      this.onStorage = event => { if (this.store.mode === 'local' && (event.key === this.store.key || event.key === null) && event.storageArea === this.store.storage) this.restore(); };
      this.onPageShow = event => { if (!event.persisted) return; this.cancelRequests(); this.details.clear(); this.restore(); };
      document.addEventListener('click', this.onClick);
      document.addEventListener('collection:reloaded', this.onReload);
      document.addEventListener('emz:saved-controls:ready', this.onControlsReady);
      document.addEventListener('shopify:section:load', this.onReload);
      window.addEventListener('storage', this.onStorage);
      window.addEventListener('pageshow', this.onPageShow);
      this.bindPanel(); this.restore();
    }
    text(key, product = '', count = '') {
      return String(this.labels[key] || '').replace(/__PRODUCT__/g, () => product).replace(/__COUNT__/g, () => String(count));
    }
    url(item) { return new URL(`${this.root}products/${encodeURIComponent(item.handle)}`, location.origin).href; }
    bindPanel() {
      // Avoid :has() in selector APIs; an unsupported selector would stop every Save control.
      const next = [...document.querySelectorAll('[data-saved-panel]')].find(panel => panel.querySelector('[data-saved-ui]')) || null;
      if (next !== this.panel) { this.resetCopy(); this.panel = next; }
      this.panel?.querySelector('[data-saved-ui]')?.removeAttribute('hidden');
    }
    restore() {
      const previousError = this.error;
      this.notice = '';
      if (this.editor) { this.error = 'editor'; this.render(); return; }
      try {
        this.items = new Map(this.store.read().map(item => [item.id, item])); this.error = '';
        document.querySelectorAll('[data-saved-feedback]').forEach(node => { node.textContent = ''; });
        const status = this.querySelector('[data-saved-status]'); if (status) status.textContent = '';
      }
      catch (error) { this.error = error.code || 'unavailable'; }
      this.render(); this.hydrate();
      if (this.error && this.error !== previousError) this.announce(this.text(this.error));
    }
    controlItem(control) { return identity({ id: control?.dataset.savedId, handle: control?.dataset.savedHandle, title: control?.dataset.savedTitle }); }
    announce(message, control) {
      this.notice = message;
      // The always-rendered hidden status is the one live region for these messages; the
      // visible feedback beside a control and the Saved page's status text are not live.
      const status = this.querySelector('[data-saved-status]'); if (status) status.textContent = message;
      if (control?.isConnected) { const feedback = control.querySelector('[data-saved-feedback]'); if (feedback) feedback.textContent = message; }
      if (this.panel) { const status = this.panel.querySelector('[data-saved-error]'); if (status) status.textContent = message; }
    }
    async change(action, item, control) {
      if (this.busy || this.editor) { if (this.editor) this.announce(this.text('editor'), control); return; }
      this.notice = '';
      if (control) control.querySelector('[data-saved-feedback]').textContent = '';
      this.busy = true; this.syncControls(); this.syncActions();
      const ticket = this.ticket;
      try {
        const next = await this.store.mutate(action, item, () => this.isConnected && ticket === this.ticket);
        if (!this.isConnected || ticket !== this.ticket) return;
        this.items = new Map(next.map(value => [value.id, value])); this.error = '';
        // Reread after releasing the lock: a later commit in another tab wins display order.
        this.restore();
        this.announce(this.text(action === 'add' ? (this.store.mode === 'session' ? 'added_session' : 'added') : action === 'remove' ? 'removed' : 'cleared', item?.title || ''), control);
      } catch (error) {
        if (!this.isConnected || ticket !== this.ticket || error.code === 'cancelled') return;
        if (error.code === 'read_error' || error.code === 'unavailable') this.error = error.code;
        this.render(); this.announce(this.text(error.code || 'write_error'), control);
      } finally {
        if (this.isConnected && ticket === this.ticket) { this.busy = false; this.syncControls(); this.syncActions(); }
      }
    }
    click(event) {
      if (event.defaultPrevented || !(event.target instanceof Element)) return;
      const button = event.target.closest('button'); if (!button || this.busy) return;
      const control = button.closest('[data-saved-control]');
      if (button.matches('[data-saved-toggle]') && control) {
        const item = this.controlItem(control); if (!item) return;
        const action = button.getAttribute('aria-pressed') === 'true' ? 'remove' : 'add';
        this.change(action, item, control);
      } else if (this.panel?.contains(button)) {
        if (button.matches('[data-saved-remove]')) {
          const item = this.items.get(button.dataset.savedRemove); if (item) this.change('remove', item);
        } else if (button.matches('[data-saved-clear], [data-saved-reset]')) {
          this.confirmAction = button.matches('[data-saved-reset]') ? 'reset' : 'clear';
          const confirm = this.panel.querySelector('[data-saved-confirm]');
          confirm.hidden = false; confirm.querySelector('[data-saved-confirm-text]').textContent = this.text(this.confirmAction === 'reset' ? 'reset_confirm' : 'confirm');
          confirm.querySelector('[data-saved-confirm-clear]').focus();
        } else if (button.matches('[data-saved-cancel]')) {
          this.panel.querySelector('[data-saved-confirm]').hidden = true;
          this.panel.querySelector(this.confirmAction === 'reset' ? '[data-saved-reset]' : '[data-saved-clear]').focus();
        } else if (button.matches('[data-saved-confirm-clear]')) {
          this.panel.querySelector('[data-saved-confirm]').hidden = true;
          this.panel.querySelector('[data-saved-heading]').focus({ preventScroll: true });
          this.change(this.confirmAction);
        } else if (button.matches('[data-saved-retry]')) { this.restore(); this.panel.querySelector('[data-saved-heading]').focus({ preventScroll: true }); }
        else if (button.matches('[data-saved-copy]')) this.copyLinks();
      }
    }
    syncControls() {
      document.querySelectorAll('[data-saved-control]').forEach(control => {
        const item = this.controlItem(control); if (!item) return;
        control.hidden = false;
        const button = control.querySelector('[data-saved-toggle]'); if (!button) return;
        const selected = this.items.has(item.id) && !this.error;
        // A toggle keeps one name; its pressed state (and filled heart) says whether it is saved.
        // The name starts with the visible label so speech input can match it.
        const label = this.text('save');
        button.setAttribute('aria-pressed', String(selected));
        if (button.textContent !== label) button.textContent = label;
        button.setAttribute('aria-label', `${label}: ${item.title}`);
        button.setAttribute('aria-disabled', String(this.busy));
        const view = control.querySelector('[data-saved-view]');
        if (view) {
          const hide = !selected && !this.error;
          if (hide && view.contains(document.activeElement)) button.focus({ preventScroll: true });
          view.hidden = hide;
        }
      });
      document.querySelectorAll('[data-saved-link]').forEach(link => {
        const count = link.querySelector('[data-saved-count]'); if (count) count.textContent = this.items.size && !this.error ? String(this.items.size) : '';
        link.setAttribute('aria-label', this.items.size && !this.error ? this.text('count', '', this.items.size) : this.text('title'));
      });
    }
    hide(element, hidden) {
      if (!element) return;
      if (hidden && element.contains(document.activeElement)) this.panel?.querySelector('[data-saved-heading]')?.focus({ preventScroll: true });
      element.hidden = hidden;
    }
    syncActions() {
      if (!this.panel) return;
      const hasItems = !this.error && this.items.size > 0;
      this.hide(this.panel.querySelector('[data-saved-clear]'), !hasItems);
      this.hide(this.panel.querySelector('[data-saved-copy]'), !hasItems);
      this.hide(this.panel.querySelector('[data-saved-retry]'), !this.error || this.editor);
      this.hide(this.panel.querySelector('[data-saved-reset]'), this.error !== 'read_error');
      if ((this.confirmAction === 'reset' && this.error !== 'read_error') || (this.confirmAction !== 'reset' && !hasItems)) this.hide(this.panel.querySelector('[data-saved-confirm]'), true);
      this.panel.querySelectorAll('[data-saved-remove], [data-saved-clear], [data-saved-copy], [data-saved-retry], [data-saved-reset], [data-saved-confirm-clear], [data-saved-cancel]').forEach(button => { button.setAttribute('aria-disabled', String(this.busy)); });
    }
    focusBounds() {
      const viewport = window.visualViewport;
      let top = viewport?.offsetTop || 0;
      const bottom = top + (viewport?.height || window.innerHeight);
      document.querySelectorAll('.emz-header-section, [id$="__header"].shopify-section-group-header-group').forEach(header => {
        const position = getComputedStyle(header).position, rect = header.getBoundingClientRect();
        if ((position === 'sticky' || position === 'fixed') && rect.height > 0 && rect.top <= top && rect.bottom > top) top = Math.min(rect.bottom, bottom);
      });
      return { top, bottom };
    }
    focusRect(element, bounds) {
      const rect = element.getBoundingClientRect();
      // On a short viewport, reveal the product name when its photo/link cannot fit.
      const label = rect.height > bounds.bottom - bounds.top - 16 && element.dataset.savedFocus === 'product' ? element.querySelector('h2') : null;
      return label ? label.getBoundingClientRect() : rect;
    }
    render(preserveVisibleFocus = false) {
      this.pending.forEach((request, id) => { if (!this.items.has(id) || this.error) { clearTimeout(request.timeout); request.controller.abort(); this.pending.delete(id); } });
      this.details.forEach((value, id) => { if (!this.items.has(id)) this.details.delete(id); });
      this.syncControls();
      if (!this.panel?.isConnected) return;
      const signature = JSON.stringify([...this.items.values()]);
      if (signature !== this.copySignature || this.error) { this.resetCopy(); this.copySignature = signature; }
      this.panel.querySelector('[data-saved-help]').textContent = this.text(this.store.mode === 'session' ? 'session_help' : 'help');
      const empty = this.panel.querySelector('[data-saved-empty]'); empty.hidden = Boolean(this.error) || this.items.size > 0;
      empty.textContent = this.text(this.store.mode === 'session' ? 'empty_session' : 'empty');
      // Leave an unchanged message alone; detail arrivals re-render often.
      const panelStatus = this.panel.querySelector('[data-saved-error]'), message = this.error ? this.text(this.error) : (this.notice || '');
      if (panelStatus.textContent !== message) panelStatus.textContent = message;
      const total = this.panel.querySelector('[data-saved-total]'); total.textContent = !this.error && this.items.size ? this.text('count', '', this.items.size) : '';
      const list = this.panel.querySelector('[data-saved-items]');
      const active = document.activeElement, oldCard = active?.closest('[data-saved-item]');
      const index = oldCard ? [...list.children].indexOf(oldCard) : -1, focusType = active?.dataset.savedFocus;
      // A detail arrival may grow earlier cards. Keep visible keyboard focus in view,
      // but do not pull someone back after they have moved focus or scrolled away.
      const bounds = preserveVisibleFocus && index >= 0 && focusType ? this.focusBounds() : null;
      const before = bounds ? this.focusRect(active, bounds) : null;
      const wasVisible = before && before.height > 0 && before.top >= bounds.top && before.bottom <= bounds.bottom;
      const fragment = document.createDocumentFragment();
      if (!this.error) this.items.forEach(item => {
        const detail = this.details.get(item.id);
        const card = document.createElement('li'); card.dataset.savedItem = item.id;
        const link = document.createElement('a'); link.href = this.url(item); link.dataset.savedFocus = 'product';
        if (detail?.image) { const img = document.createElement('img'); img.src = detail.image; img.alt = ''; img.width = 360; img.height = 360; img.loading = 'lazy'; link.append(img); }
        const title = document.createElement('h2'); title.textContent = detail?.title || item.title; link.append(title);
        const price = document.createElement('p'); price.className = 'emz-saved-price'; price.textContent = detail?.price || this.text(detail?.failed ? 'details_unavailable' : 'checking');
        card.append(link, price);
        if (detail?.price) { const stock = document.createElement('p'); stock.className = 'emz-saved-stock'; stock.textContent = this.text(detail.available ? 'available' : 'sold_out'); card.append(stock); }
        const remove = document.createElement('button'); remove.type = 'button'; remove.dataset.savedRemove = item.id; remove.dataset.savedFocus = 'remove'; remove.textContent = this.text('remove'); remove.setAttribute('aria-label', this.text('remove_label', detail?.title || item.title)); card.append(remove);
        fragment.append(card);
      });
      list.replaceChildren(fragment); list.setAttribute('aria-busy', String(this.pending.size > 0));
      this.syncActions();
      // Optional card tools restore their controls before keyboard focus is restored.
      this.panel.dispatchEvent(new CustomEvent('emz:saved-rendered'));
      if (index >= 0 && focusType) {
        const cards = [...list.children], next = cards.find(card => card.dataset.savedItem === oldCard.dataset.savedItem) || cards[Math.min(index, cards.length - 1)];
        const target = next?.querySelector(`[data-saved-focus="${focusType}"]`) || this.panel.querySelector('[data-saved-heading]');
        target.focus({ preventScroll: true });
        if (wasVisible && next?.dataset.savedItem === oldCard.dataset.savedItem && document.activeElement === target) {
          const visible = this.focusBounds(), after = this.focusRect(target, visible);
          const top = visible.top + 8, bottom = visible.bottom - 8;
          const delta = after.top < top ? after.top - top : after.bottom > bottom ? Math.min(after.bottom - bottom, Math.max(0, after.top - top)) : 0;
          if (delta) window.scrollBy({ top: delta, behavior: 'instant' });
        }
      }
    }
    hydrate() {
      if (!this.panel?.isConnected || this.error || this.editor) return;
      for (const item of this.items.values()) {
        if (this.pending.size >= 3) break;
        if (this.details.has(item.id) || this.pending.has(item.id)) continue;
        this.fetchDetails(item);
      }
    }
    async fetchDetails(item) {
      const controller = new AbortController(), ticket = this.ticket;
      const request = { controller, timeout: setTimeout(() => controller.abort(), 8000) };
      this.pending.set(item.id, request);
      try {
        const url = new URL(this.url(item)); url.searchParams.set('section_id', 'emz-shortlist-product');
        const response = await fetch(url.href, { signal: controller.signal, credentials: 'same-origin', cache: 'no-store', redirect: 'error' });
        if (!response.ok) throw new Error('detail');
        const html = await response.text(); if (html.length > 20000) throw new Error('size');
        const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-shortlist-control]');
        if (!node || node.dataset.shortlistId !== item.id || !node.dataset.shortlistTitle || !node.dataset.shortlistPrice || !['true', 'false'].includes(node.dataset.shortlistAvailable)) throw new Error('detail');
        const detail = { title: node.dataset.shortlistTitle.slice(0,500), price: node.dataset.shortlistPrice.slice(0,200), available: node.dataset.shortlistAvailable === 'true' };
        if (node.dataset.shortlistImage) { const image = new URL(node.dataset.shortlistImage, location.origin); if (image.protocol === 'https:') detail.image = image.href; }
        if (ticket === this.ticket && this.pending.get(item.id) === request && this.items.has(item.id) && !this.error) this.details.set(item.id, detail);
      } catch (_) {
        if (ticket === this.ticket && this.pending.get(item.id) === request && this.items.has(item.id) && !this.error) this.details.set(item.id, { failed: true });
      } finally {
        clearTimeout(request.timeout);
        if (ticket === this.ticket && this.pending.get(item.id) === request) { this.pending.delete(item.id); this.render(true); this.hydrate(); }
      }
    }
    resetCopy() {
      this.copyTicket = (this.copyTicket || 0) + 1;
      if (!this.panel) return;
      const status = this.panel.querySelector('[data-saved-copy-status]'); if (status) status.textContent = '';
      const fallback = this.panel.querySelector('[data-saved-copy-fallback]'); if (fallback) this.hide(fallback, true);
      const input = this.panel.querySelector('[data-saved-copy-input]'); if (input) input.value = '';
    }
    async copyLinks() {
      if (this.error || !this.items.size) return;
      this.resetCopy();
      const trigger = this.panel.querySelector('[data-saved-copy]');
      const ticket = this.copyTicket, lifecycle = this.ticket, text = [...this.items.values()].map(item => this.url(item)).join('\n');
      const current = () => this.isConnected && lifecycle === this.ticket && ticket === this.copyTicket && this.panel?.isConnected;
      try { await navigator.clipboard.writeText(text); if (current()) this.panel.querySelector('[data-saved-copy-status]').textContent = this.text('copied'); }
      catch (_) {
        if (!current()) return;
        this.panel.querySelector('[data-saved-copy-status]').textContent = this.text('copy_fallback');
        this.panel.querySelector('[data-saved-copy-fallback]').hidden = false;
        const input = this.panel.querySelector('[data-saved-copy-input]'); input.value = text;
        if (document.activeElement === trigger) { input.focus(); input.select(); }
      }
    }
    cancelRequests() { this.pending?.forEach(request => { clearTimeout(request.timeout); request.controller.abort(); }); this.pending?.clear(); }
    disconnectedCallback() {
      this.ticket = (this.ticket || 0) + 1; this.mounted = false; this.resetCopy(); this.cancelRequests();
      document.removeEventListener('DOMContentLoaded', this.ready);
      document.removeEventListener('click', this.onClick);
      document.removeEventListener('collection:reloaded', this.onReload);
      document.removeEventListener('emz:saved-controls:ready', this.onControlsReady);
      document.removeEventListener('shopify:section:load', this.onReload);
      window.removeEventListener('storage', this.onStorage);
      window.removeEventListener('pageshow', this.onPageShow);
    }
  }
  customElements.define('emz-saved-picks', SavedPicks);
})();
